import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { utcToZonedTime } from "date-fns-tz";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { FiLoader } from "react-icons/fi";
import { TbReportAnalytics } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getVendorAssessmentReport } from "../../redux/ReduxSlices/assessmentSlice";
import { getReport } from "../../redux/ReduxSlices/candidateSlice";

const VendorReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [range, setRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [containerHeight, setContainerHeight] = useState(300);
  const { vendorReportData, loading } = useSelector((state) => state.assessment);
  const details = vendorReportData?.details || [];

  useEffect(() => {
    dispatch(getVendorAssessmentReport());
  }, [dispatch]);

  const handleReport = (candidate) => {
    const id = candidate.candidate_pk;
    dispatch(getReport(id));
    navigate(`/viewreport/${id}`);
  };

  useEffect(() => {
    const updateHeight = () => {
      setContainerHeight(window.innerWidth < 768 ? 250 : 300);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const sendButtonClicked = () => {
    navigate('/hire-through-vendor')
  }

  // Filter data based on date range
  let filteredData = details;
  if (range[0].startDate && range[0].endDate) {
    const startDate = new Date(range[0].startDate);
    const endDate = new Date(range[0].endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    filteredData = details.filter((entry) => {
      const entryDate = new Date(entry.created_at);
      return entryDate >= startDate && entryDate <= endDate;
    });
  }

  // Process chart data
  let chartData = Object.values(
    filteredData.reduce((acc, entry) => {
      const dateObj = new Date(entry.created_at);
      const key = dateObj.toISOString().split("T")[0]; // YYYY-MM-DD
      const formatted = format(dateObj, "dd MMM yyyy");

      if (!acc[key]) {
        acc[key] = { date: formatted, timestamp: dateObj, count: 0 };
      }
      acc[key].count += 1;
      return acc;
    }, {})
  ).sort((a, b) => a.timestamp - b.timestamp);

  const formattedChartData = chartData.map(({ date, count }) => ({ date, count }));

  // Add dummy points if only one data point exists
  if (formattedChartData.length === 1) {
    formattedChartData.unshift({ date: "Start", count: 0 });
    formattedChartData.push({ date: "End", count: 0 });
  }

  return (
    <div className="min-h-screen p-6 max-md:p-0">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <FiLoader className="animate-spin text-[#822bda] text-4xl" />
        </div>
      )}

      {filteredData.length === 0 ? (
        <p className="text-center text-gray-500 text-sm my-6">
        </p>
      ) : (
        <motion.div className="bg-white rounded-xl shadow-lg p-6 max-md:p-1 mt-4 mb-10 max-md:mb-6"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h2 className="text-lg font-semibold text-gray-700 px-2 mb-4">
            Assessment Dispatch Trend
          </h2>
          <div className="w-full" style={{ height: containerHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedChartData}>
                <defs>
                  <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.5} />
                    <stop offset="70%" stopColor="#7C3AED" stopOpacity={0.15} />
                    <stop offset="100%" stopColor="#7C3AED" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" minTickGap={20} />
                <YAxis allowDecimals={false} domain={["auto", "auto"]} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#7C3AED"
                  fill="url(#lineGradient)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      <motion.div className="flex justify-end max-md:justify-center gap-2 mb-4"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <button
          onClick={() => setIsModalOpen(true)}
          className="text-black px-4 py-1 rounded-md text-xs font-medium transition-all duration-200 shadow border bg-violet-50 border-gray-300"
        >
          Select Date Range
        </button>
        <button
          onClick={() => {
            setRange([{ startDate: null, endDate: null, key: "selection" }]);
            setIsModalOpen(false);
          }}
          className="text-black px-4 py-1 rounded-md text-xs font-medium transition-all duration-200 shadow border bg-violet-50 border-gray-300"
        >
          Clear Filter
        </button>
      </motion.div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-xl p-6 shadow-xl w-full max-w-2xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Select Date Range
            </h3>
            <DateRange
              onChange={(item) => setRange([item.selection])}
              ranges={range}
              maxDate={new Date()}
              locale={enUS}
              showDateDisplay={false}
              editableDateInputs={false}
            />
            <div className="flex justify-end mt-6 gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}

      <motion.div className="mt-0"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {filteredData.length === 0 ? (
          <div className="text-center text-gray-500 mt-32 text-sm my-10">
            <p className="text-center text-2xl text-gray-500 mt-32 mb-4">
              No assessment records found
            </p>
            <p className="text-gray-400 mb-4">
              Once your vendor conducts an assessment for the position you opened through the 'Hire Through Vendor' section,<br /> the assessee results will appear here
            </p>
            <button className="mt-2 inline-flex items-center gap-2 px-6 py-1 border border-violet-400 text-violet-600 text-sm font-semibold rounded-md shadow-md hover:bg-violet-500 hover:text-white hover:shadow-lg transition-all duration-200 ease-in-out active:scale-95"
              onClick={sendButtonClicked}
            >
              Hire Through Vendor
            </button>
          </div>
        ) : (
          filteredData
            .slice()
            .reverse()
            .map((entry, index) => {


              const rawUTC = entry.created_at;
              const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              // Force parse as UTC
              const utcDate = new Date(rawUTC.endsWith("Z") ? rawUTC : rawUTC.replace(" ", "T") + "Z");
              // Convert to local time zone
              const localDate = utcToZonedTime(utcDate, userTimeZone);
              // Format as per local time
              const formattedDate = format(localDate, "dd MMM yyyy, hh:mm a");

              return (
                <div key={index} className="mb-8">
                  <h2 className="text-lg max-md:text-sm  mb-2 flex items-center gap-2">
                    Assessment Date :
                    <span className="bg-gray-200 text-black-700 px-3 py-1 rounded-full text-sm max-md:text-xxs shadow-sm">
                      {formattedDate}
                    </span>
                  </h2>
                  <p className="text-xs md:text-sm font-semibold text-gray-600 mb-3 mt-2">
                    Company:{" "}
                    <span className="text-gray-600 mt-2">
                      {vendorReportData.curr_company_name}
                    </span>
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border rounded-lg">
                      <thead>
                        <tr className="bg-purple-100 text-sm">
                          <th className="p-3 text-left">Email</th>
                          <th className="p-3 text-left">Score</th>
                          <th className="p-3 text-left">Assessment Started</th>
                          <th className="p-3 text-left">Assessment Completed</th>
                          <th className="p-3 text-left">Completed At</th>
                          <th className="p-3 text-left">Report Status</th>
                          <th className="p-3 text-left">Report</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t text-sm hover:bg-gray-50">
                          <td className="p-3">{entry.email}</td>
                          <td className="p-3">
                            {entry.current_role_fit_score === null
                              ? "-"
                              : entry.current_role_fit_score}
                          </td>
                          <td className="p-3">
                            {entry.is_assessment_started ? "Yes" : "No"}
                          </td>
                          <td className="p-3">
                            {entry.is_assessment_completed ? "Yes" : "No"}
                          </td>
                          <td className="p-3">
                            {entry.exam_completed_at
                              ? format(
                                new Date(entry.exam_completed_at),
                                "dd MMM yyyy, hh:mm a"
                              )
                              : "N/A"}
                          </td>
                          <td>
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium max-md:px-1 max-md:text-xs ${entry.exam_report_status === "pending"
                                ? "bg-yellow-400 text-white"
                                : entry.exam_report_status === "processing"
                                  ? "bg-blue-400 text-white"
                                  : entry.exam_report_status === "success"
                                    ? "bg-green-500 text-white"
                                    : entry.exam_report_status === "failed"
                                      ? "bg-red-500 text-white"
                                      : entry.exam_report_status === "incomplete"
                                        ? "bg-red-500 text-white"
                                        : ""
                                }`}
                            >
                              {entry.exam_report_status.charAt(0).toUpperCase() +
                                entry.exam_report_status.slice(1)}
                            </span>
                          </td>
                          <td className="p-3 text-center">
                            {entry.exam_report_status === "success" ? (
                              <div
                                className="text-green-700 font-bold flex justify-center text-sm max-md:text-xs cursor-pointer"
                                onClick={() => handleReport(entry)}
                              >
                                View Report{" "}
                              </div>
                            ) : (
                              <div
                                className="flex justify-center text-sm max-md:text-xs mt-2 font-semibold text-gray-500 max-md:mt-3"
                              >
                                Pending
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            }
            )
        )}
      </motion.div>
    </div>
  );
};

export default VendorReport;
